/**
 * @forma-studio/auth
 *
 * Modulo di autenticazione per l'ecosistema FORMA Studio.
 * Provider: Supabase Auth.
 *
 * Decisione NEXUS CORE: costruire come modulo separato.
 * La convergenza con il sistema auth di Academy (FOUNDRY/Supabase)
 * avverrà quando entrambi i sistemi saranno stabili.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';

// === USER TYPES ===

export interface User {
  id: string;
  email: string;
  displayName?: string;
  role: UserRole;
  createdAt: string;
}

export type UserRole = 'viewer' | 'designer' | 'wrapper' | 'admin';

export interface AuthSession {
  user: User;
  accessToken: string;
  expiresAt: string;
}

// === AUTH PROVIDER INTERFACE ===

export interface AuthProvider {
  signIn(email: string, password: string): Promise<AuthSession>;
  signInWithOtp(email: string): Promise<void>;
  signOut(): Promise<void>;
  getSession(): Promise<AuthSession | null>;
  onAuthStateChange(callback: (session: AuthSession | null) => void): () => void;
}

// === AUTH CONTEXT (for React apps) ===

export interface AuthContextValue {
  session: AuthSession | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithOtp: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
}

// === AUTH OPTIONS ===

export interface AuthOptions {
  storageKey?: string;
  cookieDomain?: string;
}

// === COOKIE STORAGE ADAPTER (with chunking for large sessions) ===

const CHUNK_SIZE = 3500; // safe limit under 4KB per cookie

function createCookieStorage(domain: string): Storage {
  const cookieSuffix = `; domain=${domain}; path=/; max-age=31536000; SameSite=Lax; Secure`;
  const expireSuffix = `; domain=${domain}; path=/; max-age=0; SameSite=Lax; Secure`;

  function setRawCookie(name: string, value: string, expire?: boolean) {
    document.cookie = `${name}=${value}${expire ? expireSuffix : cookieSuffix}`;
  }

  function getRawCookie(name: string): string | null {
    const match = document.cookie.match(
      new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)')
    );
    return match ? match[1] : null;
  }

  function removeChunks(key: string) {
    // Remove base key
    setRawCookie(key, '', true);
    // Remove any chunks (.0, .1, .2, ...)
    for (let i = 0; i < 10; i++) {
      if (getRawCookie(`${key}.${i}`) === null) break;
      setRawCookie(`${key}.${i}`, '', true);
    }
  }

  return {
    get length() { return 0; },
    clear() {},
    key() { return null; },

    getItem(key: string): string | null {
      // Try single cookie first
      const single = getRawCookie(key);
      if (single !== null) {
        return decodeURIComponent(single);
      }
      // Try chunked cookies
      const chunks: string[] = [];
      for (let i = 0; ; i++) {
        const chunk = getRawCookie(`${key}.${i}`);
        if (chunk === null) break;
        chunks.push(chunk);
      }
      if (chunks.length === 0) return null;
      return decodeURIComponent(chunks.join(''));
    },

    setItem(key: string, value: string) {
      // Clear old data first
      removeChunks(key);

      const encoded = encodeURIComponent(value);
      if (encoded.length <= CHUNK_SIZE) {
        // Fits in one cookie
        setRawCookie(key, encoded);
      } else {
        // Split into chunks
        for (let i = 0; i * CHUNK_SIZE < encoded.length; i++) {
          setRawCookie(`${key}.${i}`, encoded.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
        }
      }
    },

    removeItem(key: string) {
      removeChunks(key);
    },
  } as Storage;
}

// === SUPABASE AUTH PROVIDER ===

function mapSupabaseUser(supabaseUser: any): User {
  return {
    id: supabaseUser.id,
    email: supabaseUser.email || '',
    displayName: supabaseUser.user_metadata?.display_name || supabaseUser.email?.split('@')[0] || '',
    role: (supabaseUser.user_metadata?.role as UserRole) || 'viewer',
    createdAt: supabaseUser.created_at || new Date().toISOString(),
  };
}

function mapSupabaseSession(session: any): AuthSession | null {
  if (!session?.user) return null;
  return {
    user: mapSupabaseUser(session.user),
    accessToken: session.access_token || '',
    expiresAt: session.expires_at
      ? new Date(session.expires_at * 1000).toISOString()
      : '',
  };
}

export class SupabaseAuthProvider implements AuthProvider {
  private client: SupabaseClient;

  constructor(supabaseUrl: string, supabaseAnonKey: string, options?: AuthOptions) {
    const authConfig: Record<string, any> = {};

    if (options?.storageKey) {
      authConfig.storageKey = options.storageKey;
    }

    if (options?.cookieDomain) {
      authConfig.storage = createCookieStorage(options.cookieDomain);
    }

    this.client = createClient(supabaseUrl, supabaseAnonKey, {
      auth: authConfig,
    });
  }

  async signIn(email: string, password: string): Promise<AuthSession> {
    const { data, error } = await this.client.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      throw new Error(`AUTH_ERROR: ${error.message}`);
    }

    const session = mapSupabaseSession(data.session);
    if (!session) {
      throw new Error('AUTH_ERROR: No session returned.');
    }

    return session;
  }

  async signInWithOtp(email: string): Promise<void> {
    const { error } = await this.client.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
      },
    });
    if (error) {
      throw new Error(`AUTH_ERROR: ${error.message}`);
    }
  }

  async signOut(): Promise<void> {
    const { error } = await this.client.auth.signOut();
    if (error) {
      throw new Error(`AUTH_ERROR: ${error.message}`);
    }
  }

  async getSession(): Promise<AuthSession | null> {
    const { data, error } = await this.client.auth.getSession();
    if (error) {
      console.warn('Auth getSession error:', error.message);
      return null;
    }
    return mapSupabaseSession(data.session);
  }

  onAuthStateChange(callback: (session: AuthSession | null) => void): () => void {
    const { data } = this.client.auth.onAuthStateChange((_event, session) => {
      callback(mapSupabaseSession(session));
    });
    return () => data.subscription.unsubscribe();
  }
}

// === FACTORY ===

export function createAuthProvider(supabaseUrl: string, supabaseAnonKey: string, options?: AuthOptions): AuthProvider {
  return new SupabaseAuthProvider(supabaseUrl, supabaseAnonKey, options);
}
